import type { OrderBulkAction, OrderStatus } from '@flux/shared';

/**
 * Actions de ligne de l'écran Commandes (`02` §3, §4 ; table complète en
 * `01` §4). Logique pure, testée indépendamment du rendu : détermine quelles
 * actions proposer pour une commande selon son statut courant. Le serveur
 * reste la source de vérité finale (`InvalidOrderActionError` → 409 si une
 * action est tentée malgré tout, `04` §6) — cette table ne fait qu'éviter de
 * proposer une action qui échouerait à coup sûr.
 */
export type OrderRowAction = 'send-now' | 'refresh' | 'regenerate' | 'ignore' | 'reactivate' | 'mark-handled';

const AVAILABLE_FROM: Record<OrderRowAction, readonly OrderStatus[]> = {
  'send-now': ['scheduled'],
  // « Mettre à jour depuis Shopify » : partout sauf pendant une préparation,
  // qui relit elle-même Shopify.
  refresh: [
    'scheduled',
    'generated',
    'shadow',
    'queued',
    'sent',
    'integrated',
    'prepared',
    'shipped',
    'error',
    'blocked',
    'handled',
    'ignored',
  ],
  // « Renvoyer la commande à Martelet » (relecture Shopify, nouveau fichier)
  // reste possible une fois Martelet passé par là (`integrated`, `prepared`) :
  // le nouvel envoi remplace le précédent.
  regenerate: ['error', 'blocked', 'handled', 'ignored', 'sent', 'integrated', 'prepared', 'shadow'],
  ignore: ['scheduled', 'error', 'blocked'],
  reactivate: ['ignored'],
  'mark-handled': ['blocked'],
};

/** `01` §4 : action sensible (renvoi d'une commande déjà envoyée) → confirmation obligatoire (`02` §0). */
const SENSITIVE_ACTIONS: readonly OrderRowAction[] = ['regenerate'];

export function isOrderActionAvailable(action: OrderRowAction, status: OrderStatus): boolean {
  return AVAILABLE_FROM[action].includes(status);
}

export function isOrderActionSensitive(action: OrderRowAction): boolean {
  return SENSITIVE_ACTIONS.includes(action);
}

/** Actions de ligne disponibles pour une commande, dans l'ordre du tableau `01` §4. */
export function availableOrderActions(status: OrderStatus): OrderRowAction[] {
  return (Object.keys(AVAILABLE_FROM) as OrderRowAction[]).filter((action) =>
    isOrderActionAvailable(action, status),
  );
}

/** Correspondance action groupée (`ORDER_BULK_ACTIONS`) → action de ligne, pour réutiliser la même table de disponibilité. */
export const BULK_ACTION_ROW_ACTION: Record<OrderBulkAction, OrderRowAction> = {
  'send-now': 'send-now',
  regenerate: 'regenerate',
  ignore: 'ignore',
};

/** `true` si au moins une des commandes sélectionnées autorise cette action groupée (`02` §3). */
export function isBulkActionAvailableForAny(action: OrderBulkAction, statuses: readonly OrderStatus[]): boolean {
  const rowAction = BULK_ACTION_ROW_ACTION[action];
  return statuses.some((status) => isOrderActionAvailable(rowAction, status));
}
